/* ==========================================================================
   LAUZON — hero carousel (sections/lauzon-hero.liquid)

   Vertical thumbnail rail = the nav (tablist semantics, roving tabindex,
   arrows on both axes so the mobile horizontal strip keeps working).
   Transition is a clip-reveal on the frame via GSAP — direction-aware,
   quiet — with an instant class swap under prefers-reduced-motion or
   without GSAP. Class toggles are synchronous (tabber pattern) so a
   throttled tab can never strand two slides visible.

   Prev/next arrows page by hand and wrap at both ends. They are plain
   buttons, so Enter and Space come free; they take the same path as a
   thumbnail click, auto-advance included.

   Auto-advance is opt-in (data-autoplay="<seconds>"): pauses on hover and
   focus, never runs under reduced motion, and sleeps while the tab is
   hidden. Manual navigation — arrows, thumbnails, rail keys — DEFERS it
   rather than killing it: one timer, re-armed at 3× the interval, so the
   visitor keeps the slide they chose but the hero does not quit for the
   rest of the page view (2026-08-25, Dave's call — it used to stop for
   good, and anyone who touched it never saw it cycle again). The theme
   editor is the one genuinely permanent stop. ⚠️ It also suppresses
   auto-advance outright (Shopify.designMode) — judge the timing on the
   preview URL.

   First slide is server-rendered .is-active — no JS, no blank hero.
   Idempotent; re-inits on shopify:section:load; follows
   shopify:block:select in the theme editor.
   ========================================================================== */

(function () {
  'use strict';

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function init(root) {
    var carousels = root.matches && root.matches('[data-hero-carousel]')
      ? [root]
      : root.querySelectorAll('[data-hero-carousel]');
    Array.prototype.forEach.call(carousels, function (el) { setup(el); });
  }

  function setup(carousel) {
    if (carousel.dataset.heroInit) return;
    carousel.dataset.heroInit = '1';

    var tabs = Array.prototype.slice.call(carousel.querySelectorAll('[data-hero-tab]'));
    var slides = Array.prototype.slice.call(carousel.querySelectorAll('[data-hero-slide]'));
    var panes = Array.prototype.slice.call(carousel.querySelectorAll('[data-hero-pane]'));
    var caps = Array.prototype.slice.call(carousel.querySelectorAll('[data-hero-cap]'));
    var live = carousel.querySelector('[data-hero-live]');
    if (slides.length < 2) return;

    var current = 0;
    var finalize = null; // pending transition cleanup

    function announce(idx) {
      if (!live) return;
      var label = tabs[idx] ? tabs[idx].getAttribute('data-label') : '';
      live.textContent = 'Slide ' + (idx + 1) + ' of ' + slides.length + (label ? ': ' + label : '');
    }

    function setTabs(idx) {
      tabs.forEach(function (tab, i) {
        var on = i === idx;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) { tab.removeAttribute('tabindex'); } else { tab.setAttribute('tabindex', '-1'); }
      });
    }

    function swap(list, idx) {
      list.forEach(function (el, i) { el.classList.toggle('is-active', i === idx); });
    }

    function goTo(idx, opts) {
      opts = opts || {};
      idx = ((idx % slides.length) + slides.length) % slides.length;
      if (idx === current) return;
      var prev = current;
      current = idx;

      setTabs(idx);
      swap(panes, idx);
      swap(caps, idx);
      announce(idx);

      var incoming = slides[idx];
      var outgoing = slides[prev];

      if (finalize) finalize(); // interrupt: settle the previous transition now

      if (!window.gsap || reducedMotion() || opts.instant) {
        swap(slides, idx);
        return;
      }

      // Clip-reveal on the frame: wipe follows the rack direction.
      var down = idx > prev;
      var fromClip = down ? 'inset(0% 0% 100% 0%)' : 'inset(100% 0% 0% 0%)';
      var img = incoming.querySelector('img');
      var pane = panes[idx];

      incoming.classList.add('is-entering');
      outgoing.classList.add('is-active'); // stays beneath until the reveal lands

      finalize = function () {
        finalize = null;
        gsap.killTweensOf([incoming, img, pane]);
        incoming.classList.remove('is-entering');
        swap(slides, current);
        gsap.set(incoming, { clearProps: 'clipPath' });
        if (img) gsap.set(img, { clearProps: 'scale' });
      };

      gsap.fromTo(incoming,
        { clipPath: fromClip },
        {
          clipPath: 'inset(0% 0% 0% 0%)',
          duration: 0.9,
          ease: 'power2.inOut',
          overwrite: true,
          onComplete: function () { if (finalize) finalize(); }
        }
      );
      if (img) {
        gsap.fromTo(img, { scale: 1.05 }, { scale: 1, duration: 1.2, ease: 'power2.out', overwrite: true });
      }
      if (pane) {
        gsap.fromTo(pane, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.55, ease: 'power2.out', overwrite: true, clearProps: 'all' });
      }
      if (caps[idx]) {
        gsap.fromTo(caps[idx], { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.45, ease: 'power2.out', overwrite: true, clearProps: 'all' });
      }
    }

    /* ---------- Rail: click + keyboard ---------- */

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        deferAutoplay();
        goTo(i);
      });
    });

    /* ---------- Prev / next arrows ----------
       Same contract as a thumbnail click: defer auto-advance, then move one
       slide. goTo already wraps, so the ends wrap on their own. Focus stays
       on the arrow (a visitor paging with the keyboard wants to keep
       pressing it); the live region carries the position. */

    var prevBtn = carousel.querySelector('[data-hero-prev]');
    var nextBtn = carousel.querySelector('[data-hero-next]');

    function step(delta) {
      deferAutoplay();
      goTo(current + delta);
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

    var rail = carousel.querySelector('[role="tablist"]');
    if (rail) {
      // Visual orientation flips at 768px; keep the attribute honest.
      var mq = window.matchMedia('(max-width: 768px)');
      var setOrientation = function () {
        rail.setAttribute('aria-orientation', mq.matches ? 'horizontal' : 'vertical');
      };
      setOrientation();
      if (mq.addEventListener) mq.addEventListener('change', setOrientation);

      rail.addEventListener('keydown', function (e) {
        var delta = 0;
        var target = null;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') delta = 1;
        else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') delta = -1;
        else if (e.key === 'Home') target = 0;
        else if (e.key === 'End') target = tabs.length - 1;
        else return;
        e.preventDefault();
        deferAutoplay();
        var next = target !== null ? target : ((current + delta + tabs.length) % tabs.length);
        goTo(next);
        tabs[next].focus();
      });
    }

    /* ---------- Auto-advance (opt-in, slow, pausable) ----------

       Two different verbs, and the difference is the whole point:

       stopAutoplay()  sets `stopped` and never comes back. ONLY the theme
                       editor uses it — a merchant dragging blocks around
                       must not have the hero moving under them.
       deferAutoplay() leaves `stopped` alone. It clears the pending timer
                       and re-arms ONE at a longer delay, so a visitor who
                       picked a slide gets a proper look at it before the
                       hero carries on. Every manual path calls this.

       The old build called stopAutoplay() from the thumbnails, the arrows
       and the rail keydown, so the first touch killed the timer for the rest
       of the page view — which is how a hero that "auto-advances" is seen to
       sit still by anyone who interacts with it at all. */

    var seconds = parseFloat(carousel.getAttribute('data-autoplay'));
    var RESUME_FACTOR = 3;          // manual pick → 3× the interval before it resumes
    var timer = null;
    var stopped = !seconds || reducedMotion() || window.Shopify && window.Shopify.designMode;
    var paused = false;
    var nextDelay = seconds * 1000; // what the next schedule() will arm; reset once spent

    function tick() {
      timer = null;
      // A deferral is spent the moment it comes due, whether or not it could
      // act on it — from here on the cadence is the ordinary interval again.
      nextDelay = seconds * 1000;
      // Hover/focus and a hidden tab park the timer rather than rescheduling
      // it; pointerleave / focusout / visibilitychange are what restart it.
      if (stopped || paused || document.hidden) return;
      goTo(current + 1);
      schedule();
    }
    function schedule() {
      if (stopped || timer) return;
      timer = window.setTimeout(tick, nextDelay);
    }
    function stopAutoplay() {
      stopped = true;
      if (timer) { window.clearTimeout(timer); timer = null; }
    }
    /* Clearing before re-arming is what stops the defer from stacking: page
       through four slides quickly and you reset one timer four times, so
       exactly one resumption is pending — never a queue of them. Safe while
       `paused`: the timer sits armed under the cursor, tick() declines to
       advance, and pointerleave finds it already scheduled. */
    function deferAutoplay() {
      if (stopped) return;
      if (timer) { window.clearTimeout(timer); timer = null; }
      nextDelay = seconds * RESUME_FACTOR * 1000;
      schedule();
    }

    if (!stopped) {
      carousel.addEventListener('pointerenter', function () { paused = true; });
      carousel.addEventListener('pointerleave', function () { paused = false; schedule(); });
      carousel.addEventListener('focusin', function () { paused = true; });
      carousel.addEventListener('focusout', function (e) {
        if (!carousel.contains(e.relatedTarget)) { paused = false; schedule(); }
      });
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) schedule();
      });
      schedule();
    }

    /* ---------- Theme editor ---------- */

    carousel.addEventListener('lauzon:hero:block', function (e) {
      var idx = slides.findIndex(function (s) { return s.getAttribute('data-block-id') === e.detail; });
      if (idx > -1) { stopAutoplay(); goTo(idx, { instant: true }); }
    });
  }

  init(document);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var carousel = event.target.closest ? event.target.closest('[data-hero-carousel]') : null;
    if (!carousel) carousel = event.target.querySelector && event.target.querySelector('[data-hero-carousel]');
    if (!carousel || !event.detail || !event.detail.blockId) return;
    carousel.dispatchEvent(new CustomEvent('lauzon:hero:block', { detail: event.detail.blockId }));
  });
})();
